import { useEffect, useLayoutEffect, type DependencyList } from 'react';
import { useLatest } from './useLatest';

/**
 * Subscribe to something outside React (a store, a stream, a channel, an observer) while mounted, and
 * again whenever `deps` change. `subscribe` returns its unsubscribe function. `beforePaint` subscribes
 * before the browser paints, for a subscription that moves what is on screen during a gesture.
 *
 * Only for subscriptions: a value read from a store belongs in useSyncExternalStore, a DOM event in
 * useEventListener, and state derived from props in render. `subscribe` always sees the latest props and
 * state; `deps` decide when to subscribe again (the identity being followed, such as a report id).
 */
export function useSubscription(
    subscribe: () => (() => void) | void,
    deps: DependencyList,
    options: { beforePaint?: boolean } = {},
): void {
    const latest = useLatest(subscribe);
    const beforePaint = Boolean(options.beforePaint);
    // deps are the caller's: they name what the subscription follows, not what the callback reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useLayoutEffect(() => (beforePaint ? latest.current() || undefined : undefined), deps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => (beforePaint ? undefined : latest.current() || undefined), deps);
}
